import React from "react";
import PluginRegistry from "../../pluginsInfrastructure/PluginRegistry";
import {
  ComponentToBeRegistered,
  Placeholder,
  PlaceholderGroupMember,
  PlaceholderPlugin,
  PluginEnableContext,
  SupportedPluginContentfulDataType,
} from "../../pluginsInfrastructure/PluginRegistryTypes";
import loadable from "@loadable/component";
import { CtasContainerProps } from "./components/CtasContainer/CtasContainer";
import { GalleryCtaProps } from "./components/Ctas/GalleryCta";
import { StudioCtaProps } from "./components/Ctas/StudioCta";
import { AddToCartCtaProps } from "./components/Ctas/AddToCartCta";
import { GenericCtaProps } from "./components/Ctas/GenericCta";
import { BulkOrderCtaProps } from "./components/Ctas/BulkOrderCta";
import { productPageLayouts } from "../pluginOwners";
import { SignInCtaProps } from "./components/Ctas/SignInCta";
import { EditYourTemplateCtaProps } from "./components/Ctas/EditYourTemplateCta";

export const CtasComponent: ComponentToBeRegistered<CtasContainerProps> = {
  placeholder: Placeholder.Ctas,
  Component: loadable<CtasContainerProps>(() => import("./components/CtasContainer/CtasContainer")),
  name: "CtasComponent",
  enable: (context: PluginEnableContext, parentComponents: string[]) => {
    return true;
  },
};

const baseStudioData = {
  uploadedDesignCtaText: {
    contentType: SupportedPluginContentfulDataType.DictionaryEntry,
    contentfulName: "Product Page > Continue CTA",
  },
  uploadedDesignCtaDescription: {
    contentType: SupportedPluginContentfulDataType.DictionaryEntry,
    contentfulName: "Product Page > Continue CTA Description",
  },
};
export const GalleryCta: ComponentToBeRegistered<GalleryCtaProps> = {
  placeholder: PlaceholderGroupMember.CtaGallery,
  Component: loadable<GalleryCtaProps>(() => import("./components/Ctas/GalleryCta")),
  name: "GalleryCta",
  dataNeeds: {
    contentful: {
      ctaText: {
        contentType: SupportedPluginContentfulDataType.DictionaryEntry,
        contentfulName: "Product Page > Gallery CTA Text",
      },
      ctaDescription: {
        contentType: SupportedPluginContentfulDataType.DictionaryEntry,
        contentfulName: "Product Page > Gallery CTA Description",
      },
      ctaTextWithTemplate: {
        contentType: SupportedPluginContentfulDataType.DictionaryEntry,
        contentfulName: "Product Page > Gallery CTA with template",
      },
    },
  },
  enable: (context: PluginEnableContext, parentComponents: string[]) => {
    return true;
  },
};

export const StudioCta: ComponentToBeRegistered<StudioCtaProps> = {
  placeholder: PlaceholderGroupMember.CtaStudio,
  Component: loadable<StudioCtaProps>(() => import("./components/Ctas/StudioCta")),
  name: "StudioCta",
  dataNeeds: {
    contentful: {
      ctaText: {
        contentType: SupportedPluginContentfulDataType.DictionaryEntry,
        contentfulName: "Product Page > Studio CTA Text",
      },
      ctaDescription: {
        contentType: SupportedPluginContentfulDataType.DictionaryEntry,
        contentfulName: "Product Page > Studio CTA Description",
      },
      ...baseStudioData,
    },
  },
  enable: (context: PluginEnableContext, parentComponents: string[]) => {
    return true;
  },
};

export const EditYourTemplateCta: ComponentToBeRegistered<EditYourTemplateCtaProps> = {
  placeholder: PlaceholderGroupMember.CtaEditTemplate,
  Component: loadable<StudioCtaProps>(() => import("./components/Ctas/EditYourTemplateCta")),
  name: "EditYourTemplateCta",
  dataNeeds: {
    contentful: {
      ctaText: {
        contentType: SupportedPluginContentfulDataType.DictionaryEntry,
        contentfulName: "Product Page > Edit template Cta",
      },
      ctaDescription: {
        contentType: SupportedPluginContentfulDataType.DictionaryEntry,
        contentfulName: "Product Page > Edit template Description Cta",
      },
      ...baseStudioData,
    },
  },
  enable: (context: PluginEnableContext, parentComponents: string[]) => {
    return true;
  },
};

export const StudioBlankCta: ComponentToBeRegistered<StudioCtaProps> = {
  placeholder: PlaceholderGroupMember.CtaStudioBlank,
  Component: loadable<StudioCtaProps>(() => import("./components/Ctas/StudioCta")),
  name: "StudioBlankCta",
  dataNeeds: {
    contentful: {
      ctaText: {
        contentType: SupportedPluginContentfulDataType.DictionaryEntry,
        contentfulName: "Product Page V2 > Blank Studio CTA",
      },
      ctaDescription: {
        contentType: SupportedPluginContentfulDataType.DictionaryEntry,
        contentfulName: "Product Page V2 > Blank Studio CTA Description",
      },
      ...baseStudioData,
    },
  },
  enable: (context: PluginEnableContext, parentComponents: string[]) => {
    return true;
  },
};

export const StudioCustomCta: ComponentToBeRegistered<StudioCtaProps> = {
  placeholder: PlaceholderGroupMember.CtaStudioCustom,
  Component: loadable<StudioCtaProps>(() => import("./components/Ctas/StudioCta")),
  name: "StudioCustomCta",
  dataNeeds: {
    contentful: {
      ctaText: {
        contentType: SupportedPluginContentfulDataType.DictionaryEntry,
        contentfulName: "Product Page > Studio CTA Text",
      },
      ctaDescription: {
        contentType: SupportedPluginContentfulDataType.DictionaryEntry,
        contentfulName: "Product Page > Studio CTA Description",
      },
      ...baseStudioData,
    },
  },
  enable: (context: PluginEnableContext, parentComponents: string[]) => {
    return true;
  },
};

export const StudioFullBleedCta: ComponentToBeRegistered<StudioCtaProps> = {
  placeholder: PlaceholderGroupMember.CtaStudioFullBleed,
  Component: loadable<StudioCtaProps>(() => import("./components/Ctas/StudioCta")),
  name: "StudioFullBleedCta",
  dataNeeds: {
    contentful: {
      ctaText: {
        contentType: SupportedPluginContentfulDataType.DictionaryEntry,
        contentfulName: "Product Page V2 > Full Bleed Studio CTA",
      },
      ctaDescription: {
        contentType: SupportedPluginContentfulDataType.DictionaryEntry,
        contentfulName: "Product Page V2 > Full Bleed Studio CTA Description",
      },
      ...baseStudioData,
    },
  },
  enable: (context: PluginEnableContext, parentComponents: string[]) => {
    return true;
  },
};

export const StudioTemplateCta: ComponentToBeRegistered<StudioCtaProps> = {
  placeholder: PlaceholderGroupMember.CtaStudioTemplate,
  Component: loadable<StudioCtaProps>(() => import("./components/Ctas/StudioCta")),
  name: "StudioTemplateCta",
  dataNeeds: {
    contentful: {
      ctaText: {
        contentType: SupportedPluginContentfulDataType.DictionaryEntry,
        contentfulName: "Product Page > Default CTA Text",
      },
      ...baseStudioData,
    },
  },
  enable: (context: PluginEnableContext, parentComponents: string[]) => {
    return true;
  },
};

export const AddToCartCta: ComponentToBeRegistered<AddToCartCtaProps> = {
  placeholder: PlaceholderGroupMember.CtaAddToCart,
  Component: loadable<AddToCartCtaProps>(() => import("./components/Ctas/AddToCartCta")),
  name: "AddToCartCta",
  dataNeeds: {
    contentful: {
      ctaText: {
        contentType: SupportedPluginContentfulDataType.DictionaryEntry,
        contentfulName: "Product Page > Add To Cart CTA Text",
      },
      ctaDescription: {
        contentType: SupportedPluginContentfulDataType.DictionaryEntry,
        contentfulName: "Product Page > Add To Cart CTA Description",
      },
    },
  },
  enable: (context: PluginEnableContext, parentComponents: string[]) => {
    return true;
  },
};

export const AlbelliGalleryCta: ComponentToBeRegistered<SignInCtaProps> = {
  placeholder: PlaceholderGroupMember.CtaAlbelliGallery,
  Component: loadable<SignInCtaProps>(() => import("./components/Ctas/GenericCta")),
  name: "AlbelliGalleryCta",
  dataNeeds: {
    contentful: {
      ctaText: {
        contentType: SupportedPluginContentfulDataType.DictionaryEntry,
        contentfulName: "Product Page > Albelli Design CTA Text",
      },
      ctaDescription: {
        contentType: SupportedPluginContentfulDataType.DictionaryEntry,
        contentfulName: "Product Page > Albelli Design CTA Description",
      },
    },
  },
  enable: (context: PluginEnableContext, parentComponents: string[]) => {
    return true;
  },
};

export const AlbelliStudioCta: ComponentToBeRegistered<GenericCtaProps> = {
  placeholder: PlaceholderGroupMember.CtaAlbelliStudio,
  Component: loadable<GenericCtaProps>(() => import("./components/Ctas/SignInCta")),
  name: "AlbelliStudioCta",
  dataNeeds: {
    contentful: {
      ctaText: {
        contentType: SupportedPluginContentfulDataType.DictionaryEntry,
        contentfulName: "Product Page > Photobook Editor CTA Text",
      },
      ctaDescription: {
        contentType: SupportedPluginContentfulDataType.DictionaryEntry,
        contentfulName: "Product Page > Photobook Editor CTA Description",
      },
      signInDescription: {
        contentType: SupportedPluginContentfulDataType.DictionaryEntry,
        contentfulName: "Albelli Design Page > SignIn Modal Description",
      },
      signInModalTitle: {
        contentType: SupportedPluginContentfulDataType.DictionaryEntry,
        contentfulName: "Albelli Design Page > SignIn Modal Title",
      },
      signInModalPrimaryAction: {
        contentType: SupportedPluginContentfulDataType.DictionaryEntry,
        contentfulName: "Albelli Design Page > SignIn Modal Primary Action",
      },
      signInModalSecondaryAction: {
        contentType: SupportedPluginContentfulDataType.DictionaryEntry,
        contentfulName: "Albelli Design Page > SignIn Modal Secondary Action",
      },
    },
  },
  enable: (context: PluginEnableContext, parentComponents: string[]) => {
    return true;
  },
};

export const BulkOrderCta: ComponentToBeRegistered<BulkOrderCtaProps> = {
  placeholder: PlaceholderGroupMember.CtaBulkOrder,
  Component: loadable<BulkOrderCtaProps>(() => import("./components/Ctas/BulkOrderCta")),
  name: "BulkOrderCta",
  dataNeeds: {
    contentful: {
      ctaText: {
        contentType: SupportedPluginContentfulDataType.DictionaryEntry,
        contentfulName: "Product Page > Bulk Order > CTA Text",
      },
    },
  },
  enable: (context: PluginEnableContext, parentComponents: string[]) => {
    return true;
  },
};

export const UploadAndGoCta: ComponentToBeRegistered<GenericCtaProps> = {
  placeholder: PlaceholderGroupMember.CtaUploadAndGo,
  Component: loadable<GenericCtaProps>(() => import("./components/Ctas/GenericCta")),
  name: "UploadAndGoCta",
  dataNeeds: {
    contentful: {
      ctaText: {
        contentType: SupportedPluginContentfulDataType.DictionaryEntry,
        contentfulName: "Product Page V2 > Upload & Go CTA",
      },
      ctaDescription: {
        contentType: SupportedPluginContentfulDataType.DictionaryEntry,
        contentfulName: "Product Page > Upload & Go CTA Description",
      },
    },
  },
  enable: (context: PluginEnableContext, parentComponents: string[]) => {
    return true;
  },
};

export const UploadAndPrintCta: ComponentToBeRegistered<GenericCtaProps> = {
  placeholder: PlaceholderGroupMember.CtaUploadAndPrint,
  Component: loadable<GenericCtaProps>(() => import("./components/Ctas/GenericCta")),
  name: "UploadAndPrintCta",
  dataNeeds: {
    contentful: {
      ctaText: {
        contentType: SupportedPluginContentfulDataType.DictionaryEntry,
        contentfulName: "Product Page V2 > Upload and Print CTA",
      },
      ctaDescription: {
        contentType: SupportedPluginContentfulDataType.DictionaryEntry,
        contentfulName: "Product Page V2 > Upload and Print CTA Description",
      },
    },
  },
  enable: (context: PluginEnableContext, parentComponents: string[]) => {
    return true;
  },
};

export const NinetyNineDesignsCta: ComponentToBeRegistered<GenericCtaProps> = {
  placeholder: PlaceholderGroupMember.CtaNinetyNineDesigns,
  Component: loadable<GenericCtaProps>(() => import("./components/Ctas/GenericCta")),
  name: "NinetyNineDesigns",
  dataNeeds: {
    contentful: {
      ctaText: {
        contentType: SupportedPluginContentfulDataType.DictionaryEntry,
        contentfulName: "Product Page V2 > 99Designs CTA",
      },
      ctaDescription: {
        contentType: SupportedPluginContentfulDataType.DictionaryEntry,
        contentfulName: "Product Page V2 > 99Designs CTA Description",
      },
    },
  },
  enable: (context: PluginEnableContext, parentComponents: string[]) => {
    return true;
  },
};

export const DesignAssistantCta: ComponentToBeRegistered<GenericCtaProps> = {
  placeholder: PlaceholderGroupMember.CtaDesignAssistant,
  Component: loadable<GenericCtaProps>(() => import("./components/Ctas/GenericCta")),
  name: "DesignAssistantCta",
  dataNeeds: {
    contentful: {
      ctaText: {
        contentType: SupportedPluginContentfulDataType.DictionaryEntry,
        contentfulName: "Product Page V2 > DesignAssistant CTA",
      },
      ctaDescription: {
        contentType: SupportedPluginContentfulDataType.DictionaryEntry,
        contentfulName: "Product Page V2 > DesignAssistant CTA Description",
      },
    },
  },
  enable: (context: PluginEnableContext, parentComponents: string[]) => {
    return true;
  },
};

export const ctas: PlaceholderPlugin = {
  init(pluginRegistry: PluginRegistry) {
    pluginRegistry.addComponent(this, CtasComponent);
    pluginRegistry.addComponent(this, AddToCartCta);
    pluginRegistry.addComponent(this, AlbelliGalleryCta);
    pluginRegistry.addComponent(this, AlbelliStudioCta);
    pluginRegistry.addComponent(this, BulkOrderCta);
    pluginRegistry.addComponent(this, GalleryCta);
    pluginRegistry.addComponent(this, NinetyNineDesignsCta);
    pluginRegistry.addComponent(this, StudioCta);
    pluginRegistry.addComponent(this, StudioBlankCta);
    pluginRegistry.addComponent(this, StudioCustomCta);
    pluginRegistry.addComponent(this, StudioFullBleedCta);
    pluginRegistry.addComponent(this, StudioTemplateCta);
    pluginRegistry.addComponent(this, UploadAndGoCta);
    pluginRegistry.addComponent(this, UploadAndPrintCta);
    pluginRegistry.addComponent(this, DesignAssistantCta);
    pluginRegistry.addComponent(this, EditYourTemplateCta);
  },
  pluginOwner: productPageLayouts,
};
